import React, { useState, useEffect, useRef } from 'react';
import { 
  Bell, 
  BellOff,
  MessageCircle, 
  User,
  Mail,
  Clock,
  CheckCheck,
  Search,
  RefreshCw,
  Send,
  Trash2,
  Volume2,
  VolumeX,
  Star,
  StarOff,
  Phone,
  LogOut,
  Eye,
  EyeOff,
  Lock,
  Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast, Toaster } from 'sonner';
import { LogoFullHorizontal } from '../components/Logo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Notification sound
const playNotificationSound = () => {
  const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2Onp+Xi3ZtcH2LnqKdj3xzbHV/jZeYlIZ5c3R+iJSYlI2Efnp+g4uQkI2HgX5+gYaLjo6Lh4OAgISIi4yLiIWCgIKFiIqLioiGg4GDhYeJioqIhoSCg4WGiImJiIaEg4OFhoeIiIeGhIODhIWGh4eHhoWEg4SEhYaGhoaFhIODhIWFhoaGhYSEg4SEhYWGhoWFhISEhIWFhYaFhYSEhISFhYWFhYWEhISEhYWFhYWFhISEhIWFhYWFhIWEhISFhYWFhYSEhISEhYWFhYWEhISEhISFhYWFhYSEhISEhIWFhYSEhISEhISEhYWFhISEhISEhISFhYSEhISEhISEhIWFhISEhISEhISEhISEhISEhISEhISE');
  audio.volume = 0.5;
  audio.play().catch(() => {});
};

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('support_admin_token');
  return token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : {};
};

export default function SupportAdminPortal() {
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [adminInfo, setAdminInfo] = useState(null);

  // Chat state
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [isMuted, setIsMuted] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState(null);
  const messagesEndRef = useRef(null);
  const prevUnreadRef = useRef(0);

  // Check existing session on mount
  useEffect(() => {
    const token = localStorage.getItem('support_admin_token');
    if (token) {
      // Verify token is still valid
      fetch(`${API_URL}/api/support/auth/me`, {
        headers: getAuthHeaders()
      })
        .then(res => {
          if (res.ok) {
            return res.json();
          }
          throw new Error('Invalid token');
        })
        .then(data => {
          setAdminInfo(data);
          setIsAuthenticated(true);
        })
        .catch(() => {
          localStorage.removeItem('support_admin_token');
          setIsAuthenticated(false);
        });
    }
  }, []);

  // Handle login via backend API
  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setLoginError('');
    
    try {
      const response = await fetch(`${API_URL}/api/support/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: loginUsername, password: loginPassword })
      });
      
      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('support_admin_token', data.token);
        setAdminInfo(data.admin);
        setIsAuthenticated(true);
        setLoginError('');
        toast.success(`Welcome, ${data.admin.full_name}!`);
      } else {
        const error = await response.json();
        setLoginError(error.detail || 'Invalid credentials');
      }
    } catch (error) {
      setLoginError('Connection error. Please try again.');
    }
    setIsLoggingIn(false);
  };

  // Handle logout
  const handleLogout = () => {
    setIsAuthenticated(false);
    setAdminInfo(null);
    localStorage.removeItem('support_admin_token');
    toast.info('Logged out successfully');
  };

  // Fetch conversations
  const fetchConversations = async (showLoading = true) => {
    if (showLoading) setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/support/conversations`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations || []);
        
        const newUnread = (data.conversations || []).filter(c => !c.is_read).length;
        
        if (newUnread > prevUnreadRef.current && !isMuted) {
          playNotificationSound();
          toast.info('New message received!', { description: 'You have a new inquiry.' });
        }
        
        prevUnreadRef.current = newUnread;
        setUnreadCount(newUnread);
        setLastFetchTime(new Date());
      } else if (response.status === 401) {
        // Token expired, logout
        handleLogout();
        toast.error('Session expired. Please login again.');
      }
    } catch (error) {
      console.error('Error:', error);
    }
    setIsLoading(false);
  };

  // Poll for messages
  useEffect(() => {
    if (isAuthenticated) {
      fetchConversations();
      const interval = setInterval(() => fetchConversations(false), 10000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, isMuted]);

  // Auto scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedConversation]);

  // Mark as read
  const markAsRead = async (id) => {
    try {
      await fetch(`${API_URL}/api/support/conversations/${id}/read`, { 
        method: 'PUT',
        headers: getAuthHeaders()
      });
      fetchConversations(false);
    } catch (error) {}
  };

  // Mark all read
  const markAllAsRead = async () => {
    try {
      await fetch(`${API_URL}/api/support/conversations/read-all`, { 
        method: 'PUT',
        headers: getAuthHeaders()
      });
      toast.success('All marked as read');
      fetchConversations(false);
    } catch (error) {}
  };

  // Toggle star
  const toggleStar = async (id) => {
    try {
      await fetch(`${API_URL}/api/support/conversations/${id}/star`, { 
        method: 'PUT',
        headers: getAuthHeaders()
      });
      fetchConversations(false);
    } catch (error) {}
  };

  // Delete
  const deleteConversation = async (id) => {
    if (!window.confirm('Delete this conversation?')) return;
    try {
      await fetch(`${API_URL}/api/support/conversations/${id}`, { 
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      toast.success('Deleted');
      setSelectedConversation(null);
      fetchConversations(false);
    } catch (error) {}
  };

  // Send reply
  const handleSendReply = async () => {
    if (!replyMessage.trim() || !selectedConversation) return;
    try {
      await fetch(`${API_URL}/api/support/conversations/${selectedConversation._id}/reply`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ message: replyMessage })
      });
      toast.success('Reply logged. Follow up via WhatsApp/Email.');
      setReplyMessage('');
      fetchConversations(false);
    } catch (error) {
      toast.error('Failed to send');
    }
  };

  // Open WhatsApp
  const openWhatsApp = (conv) => {
    const msg = `Hi ${conv.name}! Following up on your MyHR Orbit inquiry.`;
    window.open(`https://wa.me/60175946661?text=${encodeURIComponent(msg)}`, '_blank');
  };

  // Filter conversations
  const filteredConversations = conversations.filter(conv => {
    const matchesSearch = !searchQuery || 
      conv.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      conv.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filter === 'all' ||
      (filter === 'unread' && !conv.is_read) ||
      (filter === 'starred' && conv.is_starred);
    return matchesSearch && matchesFilter;
  });

  // Format time
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  // LOGIN SCREEN
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Toaster position="top-center" richColors />
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <LogoFullHorizontal className="h-10 mx-auto mb-6" />
            <div className="inline-flex items-center px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full text-emerald-400 text-sm font-semibold mb-4">
              <Lock className="h-4 w-4 mr-2" />
              Internal Access Only
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Support Admin Portal</h1>
            <p className="text-slate-400">Manage website inquiries & leads</p>
          </div>

          <form onSubmit={handleLogin} className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-2xl p-8 space-y-5">
            <div>
              <label className="text-sm text-slate-400 mb-2 block">Username</label>
              <Input
                type="text"
                value={loginUsername}
                onChange={(e) => setLoginUsername(e.target.value)}
                placeholder="Enter username"
                className="bg-slate-900/50 border-slate-600 text-white h-12"
              />
            </div>
            
            <div>
              <label className="text-sm text-slate-400 mb-2 block">Password</label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  placeholder="Enter password"
                  className="bg-slate-900/50 border-slate-600 text-white h-12 pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {loginError && (
              <p className="text-red-400 text-sm text-center">{loginError}</p>
            )}

            <Button 
              type="submit" 
              className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl"
              disabled={isLoggingIn}
            >
              {isLoggingIn ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Signing In...
                </>
              ) : (
                'Sign In to Support Admin'
              )}
            </Button>
          </form>

          <p className="text-center text-slate-500 text-xs mt-6">
            This portal is for MyHR Orbit sales team only.
          </p>
        </div>
      </div>
    );
  }

  // MAIN DASHBOARD
  return (
    <div className="min-h-screen bg-slate-100">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <LogoFullHorizontal className="h-8" />
            <div className="h-6 w-px bg-slate-200" />
            <h1 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-emerald-500" />
              Support Inbox
            </h1>
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-full animate-pulse">
                {unreadCount} new
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setIsMuted(!isMuted); toast.info(isMuted ? 'Sound ON' : 'Sound OFF'); }}
              className={isMuted ? 'text-slate-400' : 'text-emerald-600'}
            >
              {isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
            </Button>
            
            <div className="relative">
              <Bell className={`h-5 w-5 ${unreadCount > 0 ? 'text-emerald-600 animate-bounce' : 'text-slate-400'}`} />
              {unreadCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </div>
            
            <Button variant="outline" size="sm" onClick={markAllAsRead} disabled={unreadCount === 0}>
              <CheckCheck className="h-4 w-4 mr-2" />
              Mark All Read
            </Button>
            
            <Button variant="outline" size="sm" onClick={() => fetchConversations()} disabled={isLoading}>
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>

            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-red-500 hover:text-red-600 hover:bg-red-50">
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-4">
        <div className="grid grid-cols-12 gap-4 h-[calc(100vh-120px)]">
          {/* List */}
          <div className="col-span-4 bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col">
            <div className="p-3 border-b border-slate-100 space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search..."
                  className="pl-9 bg-slate-50"
                />
              </div>
              <div className="flex gap-2">
                {['all', 'unread', 'starred'].map((f) => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full ${
                      filter === f ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {filteredConversations.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-slate-400 p-4">
                  <MessageCircle className="h-12 w-12 mb-3 opacity-50" />
                  <p className="text-sm">No conversations yet</p>
                </div>
              ) : (
                filteredConversations.map((conv) => (
                  <div
                    key={conv._id}
                    onClick={() => { setSelectedConversation(conv); if (!conv.is_read) markAsRead(conv._id); }}
                    className={`p-4 border-b border-slate-100 cursor-pointer transition-colors ${
                      selectedConversation?._id === conv._id ? 'bg-emerald-50 border-l-4 border-l-emerald-500' : 'hover:bg-slate-50'
                    } ${!conv.is_read ? 'bg-blue-50/50' : ''}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          !conv.is_read ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-600'
                        }`}>
                          <User className="h-5 w-5" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium">{conv.name || 'Anonymous'}</h3>
                            {!conv.is_read && <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>}
                            {conv.is_starred && <Star className="h-3 w-3 text-amber-500 fill-amber-500" />}
                          </div>
                          <p className="text-xs text-slate-500">{conv.email || 'No email'}</p>
                        </div>
                      </div>
                      <span className="text-[10px] text-slate-400">{formatTime(conv.updated_at)}</span>
                    </div>
                    <p className="text-sm text-slate-600 mt-2 line-clamp-2">
                      {conv.messages?.[conv.messages.length - 1]?.message || 'No messages'}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Detail */}
          <div className="col-span-8 bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col">
            {selectedConversation ? (
              <>
                <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-500 text-white rounded-full flex items-center justify-center">
                      <User className="h-5 w-5" />
                    </div>
                    <div>
                      <h2 className="font-semibold">{selectedConversation.name}</h2>
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{selectedConversation.email}</span>
                        <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{formatTime(selectedConversation.created_at)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => toggleStar(selectedConversation._id)}>
                      {selectedConversation.is_starred ? <Star className="h-4 w-4 text-amber-500 fill-amber-500" /> : <StarOff className="h-4 w-4" />}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => window.open(`mailto:${selectedConversation.email}`)}>
                      <Mail className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" className="text-green-600" onClick={() => openWhatsApp(selectedConversation)}>
                      <Phone className="h-4 w-4 mr-1" />WhatsApp
                    </Button>
                    <Button variant="outline" size="sm" className="text-red-600" onClick={() => deleteConversation(selectedConversation._id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
                  {selectedConversation.messages?.map((msg, idx) => (
                    <div key={idx} className={`flex ${msg.type === 'admin_reply' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[70%] p-3 rounded-2xl ${
                        msg.type === 'admin_reply' ? 'bg-emerald-500 text-white rounded-tr-none' : 'bg-white border rounded-tl-none'
                      }`}>
                        <p className="text-[10px] opacity-70 mb-1">
                          {msg.type === 'name' ? '👤 Name' : msg.type === 'email' ? '📧 Email' : msg.type === 'admin_reply' ? '💬 You' : '💬 Message'}
                        </p>
                        <p className="text-sm">{msg.message}</p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                <div className="p-4 border-t">
                  <div className="flex gap-2">
                    <Input
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      placeholder="Type reply (logged for reference)..."
                      onKeyDown={(e) => e.key === 'Enter' && handleSendReply()}
                    />
                    <Button onClick={handleSendReply} className="bg-emerald-500 hover:bg-emerald-600">
                      <Send className="h-4 w-4 mr-2" />Send
                    </Button>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-2">💡 Use WhatsApp or Email button to respond to leads directly</p>
                </div>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-slate-400">
                <MessageCircle className="h-16 w-16 mb-4 opacity-50" />
                <h3 className="text-lg font-medium text-slate-600">Select a conversation</h3>
              </div>
            )}
          </div>
        </div>

        {lastFetchTime && (
          <p className="text-center text-xs text-slate-400 mt-3">
            Last updated: {lastFetchTime.toLocaleTimeString()} • Auto-refreshes every 10s
          </p>
        )}
      </div>
    </div>
  );
}
